import { OptionsApi } from '@core/swagger-client';
import { CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, LoaderPage, PageHeader, PageHeaderContent } from '@core/ui';
import { getRouteApi } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactDetails } from '../../atoms';
import { ContactBody, ContactCard } from '../../molecules';
import { Contact, ContactList } from '../../organisms';

const route = getRouteApi('/$webinarId/on-demand-participants');
export const OnDemandParticipants = () => {
    const { webinarId } = route.useParams();
    const { t } = useTranslation();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const { data: participants, isLoading, error: participantsError } = useQuery(OptionsApi.insights.getOnDemandParticipants(webinarId));
    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));

    useEffect(() => {
        if (!participants) {
            return;
        }

        setContacts(
            participants.map((account) => ({
                emailAddress: account.emailAddress,
                nickname: account.nickname,
                firstName: account.firstName,
                lastName: account.lastName,
                companyName: account.companyName,
                country: account.countryCode,
                firstOnline: account.firstOnlineOnDemand,
                lastOnline: account.lastOnlineOnDemand,
                timeOnline: account.timeOnlineOnDemand,
                percentageOnline: account.percentageOnlineOnDemand
            }))
        );
    }, [participants]);

    const error = participantsError ?? webinarError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading || !contacts || isWebinarLoading || !webinar) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <ContactCard>
                <PageHeader>
                    <PageHeaderContent>
                        <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                    </PageHeaderContent>
                </PageHeader>

                <ContactBody>
                    <BackToOriginLink fallback="insights" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('Insights.OnDemand.PageHeading')}
                    </Heading>
                    <ContactDetails
                        contactCount={contacts.length}
                        singleText={t('Insights.OnDemand.AmountText.Single')}
                        multipleText={t('Insights.OnDemand.AmountText.Multiple')}
                    />
                    <ContactList
                        contacts={contacts}
                        showTimeOnline
                        webinarTitle={webinar.title}
                        exportFileName="OnDemandParticipants.xlsx"
                        exportTitle="On Demand participants"
                    />
                </ContactBody>
            </ContactCard>
        </ContainerNoTopSpace>
    );
};
